import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';
import type { PmSavedViewScope } from './savedViewsService';

export interface PmSavedViewPin {
  view_id: string;
  position: number;
}

export interface PmSavedViewPreferences {
  default_view_id: string | null;
  pins: PmSavedViewPin[];
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = ENV.API_URL;

export const pmSavedViewPreferencesService = {
  async get(params: { organization_id: string; scope: PmSavedViewScope }): Promise<{ data: PmSavedViewPreferences | null; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: params.organization_id, scope: params.scope });

      const response = await fetch(`${API_BASE_URL}/api/project-management/saved-views/preferences?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to load preferences' };
      }

      return { data: (result.data || null) as PmSavedViewPreferences | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async setDefault(input: { organization_id: string; scope: PmSavedViewScope; view_id: string | null }): Promise<{ data: { view_id: string | null } | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(`${API_BASE_URL}/api/project-management/saved-views/default`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to set default' };
      }

      return { data: result.data as { view_id: string | null }, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async pin(input: { organization_id: string; scope: PmSavedViewScope; view_id: string; position?: number }): Promise<{ error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(`${API_BASE_URL}/api/project-management/saved-views/pins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to pin view' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },

  async unpin(params: { organization_id: string; scope: PmSavedViewScope; view_id: string }): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: params.organization_id, scope: params.scope });

      const response = await fetch(
        `${API_BASE_URL}/api/project-management/saved-views/pins/${encodeURIComponent(params.view_id)}?${queryParams.toString()}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to unpin view' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },
};
